import { useMemo, useState } from "react";
import { Download, FileText } from "lucide-react";
import type {
  QuoteRequestAnswer,
  QuoteRequestPage,
} from "@shared-core/data/supplier-portal-adapter";
import type { QuoteRequestFile } from "@shared-core/data/supplier-api-types";
import { parseFormattedText } from "@shared-core/domain/formatted-text";
import {
  CLOSED_LINES,
  QUOTE_REQUEST_WORDING,
  fileSize,
  statusTone,
  weekdayDayMonth,
} from "@shared-core/domain/supplier-quote-request";
import {
  ACTION_WORDING,
  STAFF_VIEW_WORDING,
} from "@shared-core/domain/supplier-portal-wording";
import { Button } from "../../app/components/primitives/button";
import { Card } from "../../app/components/primitives/card";
import { FormattedText } from "../../app/components/patterns/FormattedText";
import { StatusPill } from "../../app/components/domain/StatusPill";
import {
  StaffViewReadOnly,
  StaffViewReadOnlyNoteOnce,
} from "../../app/components/domain/StaffViewReadOnly";
import { Banner, SectionHeading } from "../shell/states";
import { DeclineDialog } from "./DeclineDialog";
import { PricesForm } from "./PricesForm";
import { SentPrices } from "./SentPrices";

export interface QuoteRequestWrites {
  send: (answer: QuoteRequestAnswer, quoteDocument?: File) => Promise<void>;
  decline: (reason: string, requestId: string) => Promise<void>;
  /** Saves the file through the shell; rejects when it didn't arrive. */
  download: (file: QuoteRequestFile) => Promise<void>;
}

/**
 * The quote request page's body, the same by link and by login: what Pollards needs, then the prices form or the
 * prices sent, or the one line saying why the request closed. It shows nothing the response does not carry: no
 * customer, no quote number, no line (spec 31, invariant 1). `via` only adds the scope banner.
 */
export function QuoteRequestBody({
  page,
  via,
  writes,
}: {
  page: QuoteRequestPage;
  via: "link" | "login";
  writes: QuoteRequestWrites;
}) {
  const [isChanging, setIsChanging] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const specification = useMemo(
    () => parseFormattedText(page.specification),
    [page.specification],
  );
  const no = page.supplier_quote_no;
  const isClosed = page.status === "closed";
  const hasSentPrices = page.quantities.some((q) => q.cost !== null);
  const showsForm = page.can_send || (page.can_change && isChanging);

  const download = async (file: QuoteRequestFile) => {
    setDownloadError(null);
    try {
      await writes.download(file);
    } catch {
      setDownloadError(QUOTE_REQUEST_WORDING.downloadFailed);
    }
  };

  const send = async (answer: QuoteRequestAnswer, quoteDocument?: File) => {
    await writes.send(answer, quoteDocument);
    setIsChanging(false);
  };

  return (
    <article className="grid gap-4" aria-label={`Quote request ${no}`}>
      {via === "link" && (
        <Banner tone="info">{QUOTE_REQUEST_WORDING.linkScope(no)}</Banner>
      )}
      {page.status === "prices_sent" && !isChanging && (
        <Banner tone="ok">{QUOTE_REQUEST_WORDING.sent}</Banner>
      )}

      <header className="grid gap-1">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <h1 className="text-xl font-bold">
            {QUOTE_REQUEST_WORDING.title(no)}
          </h1>
          <StatusPill
            label={page.status_label}
            tone={statusTone(page.status)}
          />
          {page.reply_by && (
            <p className="text-sm text-muted-foreground">
              {QUOTE_REQUEST_WORDING.replyBy}{" "}
              <strong className="font-semibold text-foreground">
                {weekdayDayMonth(page.reply_by)}
              </strong>
            </p>
          )}
        </div>
      </header>

      {isClosed && page.closed_reason && (
        <Banner tone="warn">{CLOSED_LINES[page.closed_reason]}</Banner>
      )}

      <SectionHeading>{QUOTE_REQUEST_WORDING.whatWeNeed}</SectionHeading>
      <Card
        aria-label={QUOTE_REQUEST_WORDING.whatWeNeed}
        role="group"
        className="p-4"
      >
        <dl className="grid grid-cols-1 gap-x-3 gap-y-1 text-sm sm:grid-cols-[9rem_minmax(0,1fr)]">
          <dt className="text-muted-foreground">
            {QUOTE_REQUEST_WORDING.specification}
          </dt>
          <dd className="min-w-0 pb-2 sm:pb-0">
            <FormattedText blocks={specification} />
          </dd>
          <dt className="text-muted-foreground">
            {QUOTE_REQUEST_WORDING.deliveredTo}
          </dt>
          <dd className="pb-2 sm:pb-0">{page.delivered_to}</dd>
          {page.wanted_by && (
            <>
              <dt className="text-muted-foreground">
                {QUOTE_REQUEST_WORDING.wantedBy}
              </dt>
              <dd className="pb-2 sm:pb-0">
                {weekdayDayMonth(page.wanted_by)}
              </dd>
            </>
          )}
          {page.files.length > 0 && (
            <>
              <dt className="text-muted-foreground">
                {QUOTE_REQUEST_WORDING.files}
              </dt>
              <dd className="grid gap-1">
                {page.files.map((file) => (
                  <span
                    key={file.id}
                    className="inline-flex flex-wrap items-center gap-1"
                  >
                    <FileText
                      className="size-4 text-muted-foreground"
                      aria-hidden
                    />
                    <span>
                      {file.filename}
                      <span className="text-muted-foreground">
                        {" · "}
                        {fileSize(file.size_bytes)}
                      </span>
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      aria-label={`${ACTION_WORDING.download} ${file.filename}`}
                      onClick={() => void download(file)}
                    >
                      <Download aria-hidden />
                      {ACTION_WORDING.download}
                    </Button>
                  </span>
                ))}
                {downloadError && (
                  <span role="alert" className="text-sm text-red-600">
                    {downloadError}
                  </span>
                )}
              </dd>
            </>
          )}
        </dl>
      </Card>

      {(showsForm || hasSentPrices) && (
        <SectionHeading>{QUOTE_REQUEST_WORDING.yourPrices}</SectionHeading>
      )}
      <StaffViewReadOnlyNoteOnce sentence={STAFF_VIEW_WORDING.quoteRequest} />
      {showsForm ? (
        <>
          <StaffViewReadOnly>
            <PricesForm
              page={page}
              onSend={send}
              onCancel={page.can_change ? () => setIsChanging(false) : null}
              onDecline={
                page.can_decline && !isDeclining
                  ? () => setIsDeclining(true)
                  : null
              }
            />
          </StaffViewReadOnly>
          {isDeclining && (
            <DeclineDialog
              onSubmit={writes.decline}
              onCancel={() => setIsDeclining(false)}
            />
          )}
        </>
      ) : (
        hasSentPrices && (
          <SentPrices
            page={page}
            onChange={page.can_change ? () => setIsChanging(true) : null}
            onDownload={(file) => void download(file)}
          />
        )
      )}
    </article>
  );
}
